/**
 * 파일명 : usr0103m.tsx
 * 설명 : 사용자목록 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2020-08-10   윤성수     최초작성
 * 1.1       2024-03-19   김시현     직원명 콤보박스 추가
 * 1.2       2024-03-20   기라성     엔터키 조회 추가
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0103mAction } from '../../../redux/slice/usr/usr0103m_slice';
import { IUserItemState, IUserListItemState, IUserStCdState, IDeptCdState } from '../../../redux/models/usr/usr0103m_model';


/*  AntD  */
import { Button, Space, Select, Row, Col, Layout, Typography, Collapse, Table } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';

/*  업무  */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';



const Usr0103m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch = useAppDispatch();
    const userList     = useAppSelector((state) => state.Usr0103m.userList);
    const userStCdList = useAppSelector((state) => state.Usr0103m.userStCdList);
    const deptCdList   = useAppSelector((state) => state.Usr0103m.deptCdList);
    const inquiryCnds  = useAppSelector((state) => state.Usr0103m.inquiryCnds);
    const userDatas    = useAppSelector((state) => state.Usr0103m.userDatas);
    const current      = useAppSelector((state) => state.Usr0103m.current);
    const pageSize     = useAppSelector((state) => state.Usr0103m.pageSize);

    let tmp = 0;
    const [tableKey, setTableKey] = React.useState<number>(0);
    const [contentHeight, setContentHeight] = React.useState<number>(window.innerHeight);

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        const headerHeight = 250;  // Header 높이
        setContentHeight(window.innerHeight - headerHeight);

        const handleResize = () => {

            let tmpKey = ++tmp;
            tmp = tmpKey;
            setTableKey(tmpKey);
        };

        fn_ResetResult();
        fn_User();
        fn_DeptCds();
        setUserStCdList();

        window.addEventListener('resize', handleResize);
        return () => {
          window.removeEventListener('resize', handleResize); // cleanup
        };
    }, []);


    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text } = Typography;
    const { Panel } = Collapse;


    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>상태</div>,
                dataIndex: 'userStNm',
                key: 'userStNm',
                align: 'center',
                width: '10%',
                render: (_:string, record:IUserListItemState) => <Button name="btn_showDetail" className="link-button" size="small" onClick={(e) => onClick(e, record)}>{record.userStNm}</Button>
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>성명</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'left',
                width: '10%',
                render: (_:string, record:IUserListItemState) => <Button name="btn_showDetail" className="link-button" size="small" onClick={(e) => onClick(e, record)}>{record.userNm}</Button>
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>입사일자</div>,
                dataIndex: 'joinDt',
                key: 'joinDt',
                align: 'center',
                width: '15%',
                render: (_:string, record:IUserListItemState) => <Button name="btn_showDetail" className="link-button" size="small" onClick={(e) => onClick(e, record)}>{commonDate.maskDate(record.joinDt, 'YYYY-MM-DD')}</Button>
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>부서</div>,
                dataIndex: 'deptNm',
                key: 'deptNm',
                align: 'center',
                width: '30%',
                render: (_:string, record:IUserListItemState) => <Button name="btn_showDetail" className="link-button" size="small" onClick={(e) => onClick(e, record)}>{record.deptNm}</Button>
            },];
            return arr;
    }, []);


    /**
     * 기능 : fn_User
     * 설명 : 직원명 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_User = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: 'A'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1)
                dispatch(Usr0103mAction.setUserList(responseData.userList));
            },
        });
    };

    const userOptions = React.useCallback(() => {
        let arrUserOptions: IUserItemState[] = [];

        if (common.isAdmin()) {
            arrUserOptions.push({label: '전체', value: ''});
        }

        userList.forEach((item:IUserItemState) => {
            arrUserOptions.push({
                label: item.userNm,
                value: item.empNo
            });
        });

        return arrUserOptions;
    }, [userList]);



    /**
     * 기능 : fn_DeptCds
     * 설명 : 부서코드 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_DeptCds = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            deptStCd: 'A'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRCOMR0005',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.deptItems.sort((a:IDeptCdState, b:IDeptCdState) => (a.deptNm > b.deptNm) ? 1 : -1)
                dispatch(Usr0103mAction.setDeptCdList(responseData.deptItems));
            },
        });
    };

    const deptOptions = React.useCallback(() => {
        let arrOptions: IDeptCdState[] = [];

        if (common.isAdmin()) {
            arrOptions.push({label: '전체', value: ''});
        }

        deptCdList.forEach((item:IDeptCdState) => {
            arrOptions.push({
                label: item.deptNm,
                value: item.deptCd
            });
        });

        return arrOptions;
    }, [deptCdList]);


    const setUserStCdList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IUserStCdState[]) => {
                let arrOptions:IUserStCdState[] = [];

                arrOptions.push({label: '전체', value: ''});

                datas.forEach((item:IUserStCdState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd
                    });
                });

                dispatch(Usr0103mAction.setUserStCdList(arrOptions));
            }
        };
        common.getComCodeList('003', option);
    }

    React.useEffect(() => {
        dispatch(Usr0103mAction.setInquiryCnds({ ...inquiryCnds, userStCd : 'A' }));
        setTimeout(() => {
            fn_Search();
        }, 300);
    }, [userStCdList]);


    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        dispatch(Usr0103mAction.setCurrent(1));
        dispatch(Usr0103mAction.initState());
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Usr0103mAction.setCurrent(1));
        dispatch(Usr0103mAction.setUserDatas([]));
    };


    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any, record:IUserListItemState) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_ResetResult();   // 결과초기화
                fn_Search();        // 조회
                break;
            }

            case 'btn_Reset': {
                fn_ResetResult();
                break;
            }

            case 'btn_addUser': {
                const params:any = {};
                params.title = '사용자추가';
                params.width = 400
                params.height = 500
                params.popupId = 'usr0103p1';
                common.openPopup(params);
                break;
            }
            case 'btn_showDetail': {
                const params:any = {};
                params.title = '사용자관리';
                params.width = 400
                params.height = 500
                params.popupId = 'usr0103p1';
                params.props = { empNo : record.empNo };
                common.openPopup(params);
                break;
            }

            default:
        }
    };

    /**
     * 기능 : 조회
     * param : 
     */
    const fn_Search  = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            ...inquiryCnds
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1);
                dispatch(Usr0103mAction.setUserDatas(responseData.userList));
            },
        });
    };


    /**
      * 기능 : 입력 후 Enter key 입력 시 조회
      * 설명 : 입력컴포넌트
      * param : 
      * return : None
      */
    const handleOnKeyUp = (e:any) => {
        if (e.key === 'Enter') {
            fn_Search(); // 조회
        }
    };
    return (
        <Layout>
            <Content>
                <Row
                    align='middle'
                    justify='space-between'
                    style={{ marginTop: 10 }}
                >
                    <Space>
                        <Col>
                            <Space>
                                <label>이름</label>
                                <Select 
                                    size='small'
                                    showSearch
                                    style={{
                                        width: 80,
                                    }}
                                    optionFilterProp='label'
                                    defaultValue=''
                                    filterSort={(optionA: any, optionB: any) => {
                                        const labelA = (optionA?.label ?? '').toLowerCase();
                                        const labelB = (optionB?.label ?? '').toLowerCase();
                                        
                                        if (labelA === '전체') return -1;
                                        if (labelB === '전체') return 1;
                                        
                                        return labelA.localeCompare(labelB);
                                    }}
                                    options={userOptions()}
                                    onKeyUp={handleOnKeyUp}
                                    value={inquiryCnds.empNo}
                                    onChange={val => dispatch(Usr0103mAction.setInquiryCnds({...inquiryCnds, empNo: val}))}
                                ></Select>
                            </Space>
                        </Col>
                        <Col>
                            <Space>
                                <label>상태</label>
                                <Select
                                    size='small'
                                    style={{
                                        width: 80,
                                    }}
                                    optionFilterProp='label'
                                    options={userStCdList}
                                    onKeyUp={handleOnKeyUp}
                                    onChange={val => {
                                        dispatch(Usr0103mAction.setInquiryCnds({...inquiryCnds, userStCd: val}))
                                    }}
                                    value={inquiryCnds.userStCd}
                                />
                            </Space>
                        </Col>
                    </Space>
                </Row>
                <Row>
                    <Col flex='9.5'>
                        <Space>
                            <label>부서</label>
                            <Select
                                size='small'
                                style={{
                                    width: 200,
                                }}
                                optionFilterProp='label'
                                options={deptOptions()}
                                onKeyUp={handleOnKeyUp}
                                onChange={val => {
                                    dispatch(Usr0103mAction.setInquiryCnds({...inquiryCnds, deptCd: val}))
                                }}
                                value={inquiryCnds.deptCd}
                            />
                        </Space>
                    </Col>
                    <Col flex='0.5'>
                        <Button
                            size='small'
                            name='btn_Search'
                            className='background-color-primary-button'
                            icon={<SearchOutlined />}
                            onClick={(e:any) => onClick(e, null)}
                        />
                    </Col>
                </Row>
                <Row align='middle' style={{ height: 50 }}>
                    <Col flex='9.5' >
                        <Space>
                            <img className='title-icon' alt='' />
                            <Text strong>사용자목록</Text>
                        </Space>
                    </Col>
                    <Col flex='0.5'>
                        <Space>
                            {common.isAdmin() ?
                            <Button
                                size='small'
                                className='background-color-primary-button'
                                onClick={(e:any) => onClick(e, null)}
                                name='btn_addUser'
                                icon={<PlusOutlined/>}
                            /> : null}
                        </Space>
                    </Col>
                </Row>
                <Row justify='center'>
                    <Col>
                        <div key={tableKey} style={{ height: contentHeight, display: 'flex', flexDirection: 'column', width : '100%', flex: '1 1 auto', overflowY: 'auto' }}>
                            <Table
                                dataSource={userDatas}
                                key='empNo'
                                columns={columns()}
                                size='small'
                                style={{width:'100%'}}
                                scroll={{y:550}}
                                pagination={{
                                    pageSize: pageSize,
                                    current: current,
                                    showTotal : () => '총 ' + userDatas.length + ' 건',
                                    onChange: (page, pageSize) => {
                                        dispatch(Usr0103mAction.setCurrent(page));
                                        dispatch(Usr0103mAction.setPageSize(pageSize));
                                    },
                                }}
                            />
                        </div>
                    </Col>
                </Row>
            </Content>
        </Layout>
    );
};

export default Usr0103m;

